import { useCallback, useEffect, useRef } from 'react';
import { Span, SpanStatusCode } from '@opentelemetry/api';
import { useIsFeatureEnabled } from '../features/feature-enablement/featureEnablement';
import { tracer } from './openTelemetry';

export const useSendMetricsToOTEL = ({
  timerCondition,
  status,
  error,
  event,
}: {
  timerCondition: boolean;
  status: 'loading' | 'error' | 'success';
  error?: unknown;
  event: { name: string };
}) => {
  const isEnabled = useIsFeatureEnabled('open-telemetry');
  const spanRef = useRef<Span | null>(null);

  const startTimer = useCallback(() => {
    // Start span with attributes
    spanRef.current = tracer.startSpan(event.name, {
      attributes: {
        'user_agent.original': navigator.userAgent,
        'r8.sli.v1.event': event.name,
      },
    });
  }, [event.name]);

  const endTimer = useCallback(() => {
    if (spanRef.current) {
      const { current: span } = spanRef;

      // Set span status based on the provided status
      const spanStatus =
        status === 'success'
          ? { code: SpanStatusCode.OK }
          : {
              code: SpanStatusCode.ERROR,
              ...(error
                ? {
                    message: `Load failed with status: ${status} and error: ${JSON.stringify(error)}`,
                  }
                : {}),
            };

      span.setStatus(spanStatus);
      span.end();
    }
  }, [status, error]);

  // Handle timer condition changes
  useEffect(() => {
    if (isEnabled) {
      if (!timerCondition) {
        startTimer();
      } else {
        endTimer();
      }
    }
  }, [isEnabled, timerCondition, startTimer, endTimer]);

  // Cleanup function to ensure span is ended if component unmounts
  useEffect(() => {
    const { current: span } = spanRef;

    if (!span) return () => {};

    return () => {
      span.end();
    };
  }, []);
};
